import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { ApiError } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { Button } from '../../ui/index.js'
import { usePageTitle } from '../../lib/page-title.js'
import { AuthLayout } from './AuthLayout.js'

interface Workspace {
  readonly organisationId: string
  readonly name: string
}

export function ChooseWorkspace() {
  usePageTitle('Choose workspace')
  const { api, signIn } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const code = params.get('code') ?? ''

  const [workspaces, setWorkspaces] = useState<readonly Workspace[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (code === '') {
      setError('That sign-in link is incomplete. Start again from the sign-in page.')
      return
    }
    let live = true
    api
      .get<{ workspaces: Workspace[] }>(`/api/auth/oauth/workspaces?code=${encodeURIComponent(code)}`)
      .then((r) => {
        if (live) setWorkspaces(r.workspaces)
      })
      .catch((err) => {
        if (live) setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.')
      })
    return () => {
      live = false
    }
  }, [api, code])

  async function choose(organisationId: string) {
    // The code is single use, so a second click while the first is in flight would
    // spend it and surface a confusing "already been used" error.
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.post<{ accessToken: string; refreshToken: string }>(
        '/api/auth/oauth/select-workspace',
        { code, organisationId },
      )
      await signIn(result)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.')
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title="Choose your workspace"
      subtitle="Your Google account belongs to more than one Ovrsee workspace."
      footer={<Link to="/login">← Back to sign in</Link>}
    >
      {error !== null ? (
        <div role="alert" style={{ color: 'var(--negative)', fontSize: 13, marginBottom: 16 }}>{error}</div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {workspaces.map((w) => (
          <Button
            key={w.organisationId}
            type="button"
            disabled={busy}
            onClick={() => { void choose(w.organisationId) }}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '12px 14px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              fontSize: 14,
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {w.name}
          </Button>
        ))}
      </div>
    </AuthLayout>
  )
}
